import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDownOutlined';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUpOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Collapse, Grid, InputAdornment, Stack, Typography } from '@mui/material';
import { useAtom } from 'jotai';
import { debounce } from 'lodash';
import { FC, useCallback, useMemo } from 'react';

import { ID } from 'domain/types/ID';
import { CustomerJobStatus } from 'domain/entities/CustomerJob';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { atomWithToggleAndStorage } from 'infrastructure/utils/atomWithToggleAndStorage';
import DEFAULT_DEBOUNCE_TIME from 'infrastructure/utils/defaultDebounceTime';
import { Button, TextField } from 'targets/web/components';
import { FilterCustomerAutocomplete } from 'targets/web/modules/dashboard/components';
import { StationAutocomplete } from 'targets/web/modules/dashboard/components/FiltersContainer/StationAutocomplete';
import { componentShadows } from 'targets/web/theme/shadows';
import {
  PageType,
  useCustomerJobViewFilters,
} from 'targets/customer-portal/modules/jobs/hooks/useCustomerJobViewFilters';

import StatusPicker from './StatusPicker';

const filtersCollapsedAtom = atomWithToggleAndStorage('isFilterContainerCollapsed', false);

export interface JobFiltersFields {
  phrase: string;
  customerId: ID;
  stationIds: ID[];
  status: CustomerJobStatus;
}

interface JobFiltersProps {
  initialValues?: Partial<JobFiltersFields>;
  onFiltersChange: (values: Partial<JobFiltersFields>) => void;
  pageType: PageType;
}

const JobFilters: FC<JobFiltersProps> = ({ initialValues, onFiltersChange, pageType }) => {
  const [filtersCollapsed, toggleFilters] = useAtom(filtersCollapsedAtom);
  const onClickHandler = useCallback(() => toggleFilters(), [toggleFilters]);
  const t = useTranslationPrefix('components.filters_container');
  const debouncedSearchHandler = useMemo(
    () =>
      debounce((e) => {
        const phrase = e.target.value.trim();
        onFiltersChange({ phrase: phrase.length > 2 ? phrase : undefined });
      }, DEFAULT_DEBOUNCE_TIME),
    [onFiltersChange],
  );

  const { allowedStatuses, filters } = useCustomerJobViewFilters(pageType);

  return (
    <Stack
      width={1}
      data-testname="filtersContainer"
      sx={{
        padding: 6,
        borderRadius: 1,
        backgroundColor: 'background.default',
        boxShadow: componentShadows.card,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">{t('header')}</Typography>
        <Button
          size="small"
          variant="text"
          endIcon={filtersCollapsed ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          onClick={onClickHandler}
        >
          {filtersCollapsed ? t('hide_filters') : t('show_filters')}
        </Button>
      </Stack>

      <Collapse in={filtersCollapsed}>
        <Box mt={4}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <TextField
                defaultValue={initialValues?.phrase}
                onChange={debouncedSearchHandler}
                placeholder={t('search_label')}
                data-testname="searchInput"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FilterCustomerAutocomplete
                label={t('customer_label')}
                onChange={(customerId) => {
                  onFiltersChange({ customerId });
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StationAutocomplete
                onChange={(stations) => {
                  onFiltersChange({ stationIds: stations.map((station) => station.id) });
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatusPicker
                allowedStatuses={allowedStatuses}
                value={filters.status}
                onChange={(status) => {
                  onFiltersChange({ status });
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Stack>
  );
};

export default JobFilters;
